// src/components/WeatherAlertBanner.jsx
// Shows pending weather alerts for one or more teams. Promoted to a
// global surface (AppShell) in May 2026 so coaches see an alert no
// matter which page they're on — previously it only rendered on the
// (now-unmounted) Dashboard, which meant time-sensitive alerts could
// hide while a coach was browsing the schedule or registrations.
//
// The actual per-alert UI (Still On / Cancel / Postpone + follow-up)
// lives in ./WeatherAlertCard so it can be reused on the focused
// response page (src/pages/WeatherAlertResponse.jsx) that email/push
// CTAs deep-link into.
//
// Props:
//   - teamId   : single team id (legacy back-compat)
//   - teamIds  : array of team ids (preferred, for multi-team coaches)
// Pass either; if both given, teamIds wins. With no ids the component
// renders null.

import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/firebase';

import WeatherAlertCard from './WeatherAlertCard';

export default function WeatherAlertBanner({ teamId, teamIds }) {
  const [alerts, setAlerts] = useState([]);

  // Resolve to a stable list of ids. Use teamIds if provided, fall back
  // to single teamId. Sort to keep the dep array stable so we don't
  // resubscribe every render when the parent passes a fresh array.
  const idList = (teamIds && teamIds.length > 0)
    ? Array.from(new Set(teamIds.filter(Boolean))).sort()
    : (teamId ? [teamId] : []);
  const idKey = idList.join(',');

  useEffect(() => {
    if (idList.length === 0) return;

    // Firestore 'in' query supports up to 30 values. If more, chunk
    // and merge; in practice almost no coach has 30+ teams so the
    // single-query path is the hot path.
    const chunks = [];
    for (let i = 0; i < idList.length; i += 30) {
      chunks.push(idList.slice(i, i + 30));
    }

    const buckets = chunks.map(() => []);
    const flush = () => {
      const merged = buckets.flat();
      // De-dupe by alert id (same alert shouldn't appear twice across
      // chunks, but defensive).
      const seen = new Set();
      const uniq = [];
      for (const a of merged) {
        if (seen.has(a.id)) continue;
        seen.add(a.id);
        uniq.push(a);
      }
      setAlerts(uniq);
    };

    const unsubs = chunks.map((chunk, idx) => {
      const q = query(
        collection(db, 'weatherAlerts'),
        where('teamId', 'in', chunk),
        where('status', '==', 'pending'),
      );
      return onSnapshot(q, snap => {
        buckets[idx] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        flush();
      }, () => {});
    });

    return () => unsubs.forEach(u => u && u());
    // idKey is the stable string-encoded version of the list, so we
    // don't resubscribe when the parent passes a fresh-reference array
    // with the same contents.
  }, [idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!alerts.length) return null;

  return (
    <div className="mb-5 space-y-3">
      {alerts.map(alert => (
        <WeatherAlertCard key={alert.id} alert={alert} teamId={alert.teamId} dense />
      ))}
    </div>
  );
}
